import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { NumberInput } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import { AddEntryButton } from './AddEntryButton';
import { useBlankStart } from './use-blank-start';

interface InfluencerSectionProps {
  influencerReach: number;
  influencerReachCpm: number;
  influencerImpressions: number;
  influencerImpressionsCpm: number;
  influencerEngagements: number;
  influencerEngagementsCpm: number;
  onInfluencerReachChange: (value: number) => void;
  onInfluencerReachCpmChange: (value: number) => void;
  onInfluencerImpressionsChange: (value: number) => void;
  onInfluencerImpressionsCpmChange: (value: number) => void;
  onInfluencerEngagementsChange: (value: number) => void;
  onInfluencerEngagementsCpmChange: (value: number) => void;
  canEdit: boolean;
  numberFormat?: NumberFormatType;
}

export function InfluencerSection({
  influencerReach,
  influencerReachCpm,
  influencerImpressions,
  influencerImpressionsCpm,
  influencerEngagements,
  influencerEngagementsCpm,
  onInfluencerReachChange,
  onInfluencerReachCpmChange,
  onInfluencerImpressionsChange,
  onInfluencerImpressionsCpmChange,
  onInfluencerEngagementsChange,
  onInfluencerEngagementsCpmChange,
  canEdit,
  numberFormat = 'eu',
}: InfluencerSectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const [expanded, expand] = useBlankStart(
    influencerReach !== 0 ||
      influencerReachCpm !== 0 ||
      influencerImpressions !== 0 ||
      influencerImpressionsCpm !== 0 ||
      influencerEngagements !== 0 ||
      influencerEngagementsCpm !== 0
  );

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          Influencers
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            HQ
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Influencer and creator collaboration reach, impressions, and engagement metrics.
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      {!expanded && (
        <div className="max-w-2xl md:col-span-2">
          <AddEntryButton label="Influencers" onClick={expand} disabled={!canEdit} />
        </div>
      )}

      {expanded && (
        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
          {/* Influencer Reach */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Influencer Reach
                  <Tooltip {...tooltipData.influencerReach} wide={true} />
                </>
              }
              value={influencerReach}
              onChange={(value) => onInfluencerReachChange(typeof value === 'number' ? value : 0)}
              decimals={0}
              numberFormat={numberFormat}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* Influencer Reach CPM */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Reach CPM
                  <Tooltip {...tooltipData.influencerReachCpm} wide={true} />
                </>
              }
              value={influencerReachCpm}
              onChange={(value) =>
                onInfluencerReachCpmChange(typeof value === 'number' ? value : 0)
              }
              decimals={2}
              numberFormat={numberFormat}
              suffix={getCurrencySymbol(baseCurrency)}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* Influencer Impressions */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Influencer Impressions
                  <Tooltip {...tooltipData.influencerImpressions} wide={true} />
                </>
              }
              value={influencerImpressions}
              onChange={(value) =>
                onInfluencerImpressionsChange(typeof value === 'number' ? value : 0)
              }
              decimals={0}
              numberFormat={numberFormat}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* Influencer Impressions CPM */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Impressions CPM
                  <Tooltip {...tooltipData.influencerImpressionsCpm} wide={true} />
                </>
              }
              value={influencerImpressionsCpm}
              onChange={(value) =>
                onInfluencerImpressionsCpmChange(typeof value === 'number' ? value : 0)
              }
              decimals={2}
              numberFormat={numberFormat}
              suffix={getCurrencySymbol(baseCurrency)}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* Influencer Engagements */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Influencer Engagements
                  <Tooltip {...tooltipData.influencerEngagements} wide={true} />
                </>
              }
              value={influencerEngagements}
              onChange={(value) =>
                onInfluencerEngagementsChange(typeof value === 'number' ? value : 0)
              }
              decimals={0}
              numberFormat={numberFormat}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>

          {/* Influencer Engagements CPM */}
          <div className="sm:col-span-3">
            <NumberInput
              label={
                <>
                  Engagements CPM
                  <Tooltip {...tooltipData.influencerEngagementsCpm} wide={true} />
                </>
              }
              value={influencerEngagementsCpm}
              onChange={(value) =>
                onInfluencerEngagementsCpmChange(typeof value === 'number' ? value : 0)
              }
              decimals={2}
              numberFormat={numberFormat}
              suffix={getCurrencySymbol(baseCurrency)}
              placeholder="0"
              disabled={!canEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
}
